import React, { useState } from 'react';
import { Sparkles, X, Handshake } from 'lucide-react';

/**
 * SynergyPanel — a quiet "Open the synergy" reveal on /shop product
 * cards for the Conditioner (and any formula that participates in the
 * Onion Seed × Rice Bran × Sunflower Lecithin protocol).
 *
 * It opens a small modal that names the three actors and frames
 * Sunflower Lecithin as "The Peacemaker" — the cold-process bridge
 * between oil and water. Connects the chemistry to the brand's
 * "Vedic Biophysics" + "Protocol of Harmony" narratives.
 *
 * The panel is opt-in per product via the `actors` prop so future
 * formulas can carry their own synergy narrative.
 */

const DEFAULT_ACTORS = [
  {
    role: 'Native sulfur',
    name: 'Onion Seed',
    note: 'Sulfur · keeper of the root. Wakes the follicle.',
  },
  {
    role: 'UV armor',
    name: 'Rice Bran',
    note: 'Ferulic acid · keeper of the canopy. Shields the strand.',
  },
  {
    role: 'The Peacemaker',
    name: 'Sunflower Lecithin',
    note:
      'Phosphatidylcholine · cold-process physics. Bridges the oil-and-water divide so the formula holds without heat or force.',
    peacemaker: true,
  },
];

const SynergyPanel = ({
  productName,
  actors = DEFAULT_ACTORS,
  testIdSuffix = '',
}) => {
  const [open, setOpen] = useState(false);
  const testId = `synergy${testIdSuffix}`;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`${testId}-open`}
        className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-[#D4AF5A] hover:text-[#7C9CB1] transition-colors"
      >
        <Sparkles size={11} strokeWidth={1.6} />
        Open the synergy
      </button>

      {open && (
        <div
          data-testid={`${testId}-modal`}
          role="dialog"
          aria-modal="true"
          aria-label={`Synergy of ${productName}`}
          className="fixed inset-0 z-50 flex items-center justify-center px-4 py-10"
        >
          {/* Backdrop */}
          <button
            type="button"
            aria-label="Close synergy panel"
            data-testid={`${testId}-backdrop`}
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-[#0E1A22]/70 backdrop-blur-sm"
          />

          {/* Panel */}
          <div className="relative w-full max-w-2xl bg-[#F4F8F9] border border-[#C1D7E2] shadow-[0_30px_80px_-30px_rgba(14,26,34,0.5)]">
            <span
              aria-hidden
              className="absolute top-0 left-8 right-8 h-px"
              style={{
                background:
                  'linear-gradient(90deg, transparent, #D4AF5A 35%, #D4AF5A 65%, transparent)',
              }}
            />
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid={`${testId}-close`}
              className="absolute top-4 right-4 text-[#7C9CB1]/60 hover:text-[#7C9CB1]"
              aria-label="Close"
            >
              <X size={18} strokeWidth={1.5} />
            </button>

            <div className="px-8 md:px-10 pt-10 pb-8">
              <p className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#D4AF5A] font-semibold mb-3">
                The Protocol of Harmony · synergy
              </p>
              <h3 className="font-serif text-3xl md:text-4xl text-[#7C9CB1] font-light leading-tight mb-4">
                {productName}
                <br />
                <em className="text-[#90B0BF]">no botanical alone.</em>
              </h3>
              <p className="font-serif italic text-[#7C9CB1]/85 leading-relaxed mb-8">
                In this formulation, no single botanical tries to be what
                it is not. The native sulfur of the Onion Seed does not
                compete with the UV armor of the Rice Bran; they
                cooperate. Sunflower Lecithin, the Peacemaker, bridges
                the oil-and-water divide through cold-process physics —
                so the ritual holds together without heat or force.
              </p>

              <ol
                data-testid={`${testId}-actors`}
                className="space-y-4"
              >
                {actors.map((a) => (
                  <li
                    key={a.name}
                    data-testid={`${testId}-actor-${a.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`flex items-start gap-4 border-l-2 pl-4 ${a.peacemaker ? 'border-[#D4AF5A]' : 'border-[#C1D7E2]'}`}
                  >
                    {a.peacemaker ? (
                      <Handshake
                        size={20}
                        strokeWidth={1.4}
                        className="text-[#D4AF5A] mt-1 shrink-0"
                      />
                    ) : (
                      <span
                        aria-hidden
                        className="inline-block w-2 h-2 rounded-full bg-[#7C9CB1] mt-2 shrink-0"
                      />
                    )}
                    <div>
                      <p
                        className={`font-mono text-[10px] tracking-[0.26em] uppercase ${a.peacemaker ? 'text-[#D4AF5A] font-semibold' : 'text-[#90B0BF]'}`}
                      >
                        {a.role}
                      </p>
                      <p className="font-serif text-xl text-[#7C9CB1] font-light leading-tight">
                        {a.name}
                      </p>
                      <p className="mt-1 text-sm text-[#7C9CB1]/85 font-light leading-relaxed">
                        {a.note}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>

              <p
                data-testid={`${testId}-biophysics`}
                className="mt-8 pt-6 border-t border-[#C1D7E2] font-mono text-[10px] tracking-[0.28em] uppercase text-[#90B0BF] leading-relaxed"
              >
                Vedic Biophysics · cooperative chemistry teaches the
                ritual a smaller refusal: the daily act of letting your
                native nature be what it already is.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SynergyPanel;
